import { useState } from 'react';
import {
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Edit as EditIcon } from '@mui/icons-material';
import { RowTableCell } from '../../../common/components';
import { useToggleReconciliation } from '../../../hooks/useBankMovements';
import { formatCurrency, formatDate } from '../../bank-movements/utils/formatters';

const CashRetentionRow = ({ movement, onEdit, onRefresh }) => {
  const theme = useTheme();
  const toggleReconciliationMutation = useToggleReconciliation();
  const [editing, setEditing] = useState(false);

  const handleToggle = async () => {
    try {
      await toggleReconciliationMutation.mutateAsync(movement.uuid);
      onRefresh?.();
    } catch (error) {
      console.error('Error toggling reconciliation:', error);
    }
  };

  const handleEdit = async () => {
    try {
      setEditing(true);
      await Promise.resolve(onEdit?.(movement));
    } finally {
      setEditing(false);
    }
  };

  const amountColor = Number(movement.amount ?? 0) >= 0 ? 'success.main' : 'error.main';

  return (
    <TableRow
      hover
      sx={{
        backgroundColor: movement.is_reconciled
          ? 'rgba(76, 175, 80, 0.08)'
          : theme.palette.background.paper,
        borderLeft: movement.is_reconciled
          ? `4px solid ${theme.palette.success.main}`
          : `4px solid ${theme.palette.error.main}`,
      }}
    >
      <RowTableCell>{formatDate(movement.emission_date)}</RowTableCell>
      <RowTableCell>{movement.due_date ? formatDate(movement.due_date) : '-'}</RowTableCell>
      <RowTableCell>{movement.movement_type_name || '-'}</RowTableCell>
      <RowTableCell>{movement.concept || '-'}</RowTableCell>
      <RowTableCell>{movement.voucher_number || '-'}</RowTableCell>
      <RowTableCell>{movement.legal_entity_name || '-'}</RowTableCell>
      <RowTableCell align="right">
        <Typography color={amountColor} fontFamily="monospace">
          {formatCurrency(movement.amount ?? 0, 2)}
        </Typography>
      </RowTableCell>
      <RowTableCell align="center">
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip
            title={movement.is_reconciled ? 'Marcar como pendiente' : 'Marcar como conciliado'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={handleToggle}
                disabled={toggleReconciliationMutation.isPending}
                sx={{
                  color: movement.is_reconciled ? theme.palette.success.main : theme.palette.error.main,
                }}
              >
                {toggleReconciliationMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : movement.is_reconciled ? (
                  <CheckCircleIcon />
                ) : (
                  <CancelIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Editar movimiento" arrow>
            <span>
              <IconButton size="small" onClick={handleEdit} color="primary" disabled={editing}>
                {editing ? <CircularProgress size={20} /> : <EditIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </RowTableCell>
    </TableRow>
  );
};

export default CashRetentionRow;
